import type {
  DebriefEventType,
  DebriefAnalysis,
  DebriefNotRequiredReason,
  DebriefReminder,
  FollowUp,
  RealEstateCase
} from "./types";

export const shotScoreReviewFromAnalysis = (analysis?: DebriefAnalysis) => ({
  identity: analysis?.shotScoreRecommendation?.identity.value ?? false,
  value: analysis?.shotScoreRecommendation?.value.value ?? false,
  acquisition: analysis?.shotScoreRecommendation?.acquisition.value ?? false,
  nextStep: analysis?.shotScoreRecommendation?.nextStep.value ?? false
});

export const DEBRIEF_DEFAULT_DURATION_MINUTES: Record<DebriefEventType, number> = {
  property_viewing: 45,
  acquisition_meeting: 45,
  client_meeting: 45,
  financial_meeting: 45,
  negotiation: 45,
  follow_up_meeting: 45
};

export const DEBRIEF_REASON_LABELS: Record<DebriefNotRequiredReason, string> = {
  meeting_cancelled: "Stretnutie bolo zrušené",
  meeting_rescheduled: "Stretnutie bolo preložené",
  client_no_show: "Klient neprišiel",
  no_useful_interaction: "Bez užitočnej interakcie",
  duplicate_event: "Duplicitná udalosť",
  personal_non_business: "Osobná / neobchodná udalosť",
  other: "Iný dôvod"
};

const normalize = (value: string) => value.toLocaleLowerCase("sk-SK");

const isProductionActivity = (text: string) =>
  text.includes("foten") ||
  text.includes("fotograf") ||
  text.includes("príprava inzerc") ||
  text.includes("priprava inzerc") ||
  text.includes("upload fotograf") ||
  text.includes("tvorba inzer") ||
  text.includes("administrat");

export const isDebriefPurpose = (purpose: string) => {
  const text = normalize(purpose);
  return !isProductionActivity(text) && (text.includes("obhliad") || text.includes("stretn") || text.includes("rokovan"));
};

export const classifyDebriefEvent = (caseItem: RealEstateCase, followUp: FollowUp): DebriefEventType | undefined => {
  const text = normalize(`${caseItem.stage} ${caseItem.status} ${caseItem.nextStep} ${followUp.purpose}`);
  if (isProductionActivity(text)) return undefined;
  if (text.includes("obhliad")) return "property_viewing";
  if (text.includes("rokovan")) return "negotiation";
  if (text.includes("finan") && text.includes("stretn")) return "financial_meeting";
  if ((text.includes("nábor") || text.includes("nabor") || text.includes("akviz")) && text.includes("stretn")) return "acquisition_meeting";
  if (text.includes("follow-up") && text.includes("stretn")) return "follow_up_meeting";
  if (text.includes("stretn")) return "client_meeting";
  return undefined;
};

export const debriefEventLabel = (type: DebriefEventType) => ({
  property_viewing: "Obhliadka",
  acquisition_meeting: "Náborové stretnutie",
  client_meeting: "Stretnutie s klientom",
  financial_meeting: "Finančné stretnutie",
  negotiation: "Rokovanie",
  follow_up_meeting: "Follow-up stretnutie"
})[type];

const localDateTime = (date: string, time: string) => {
  const parsed = new Date(`${date}T${time}:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const stableToken = (value: string) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

export const buildDebriefReminder = (
  caseItem: RealEstateCase,
  followUp: FollowUp,
  now = new Date()
): DebriefReminder | undefined => {
  const eventType = classifyDebriefEvent(caseItem, followUp);
  const start = localDateTime(followUp.date, followUp.time);
  if (!eventType || !start) return undefined;

  const explicitEnd = followUp.endTime ? localDateTime(followUp.date, followUp.endTime) : undefined;
  const end = explicitEnd && explicitEnd > start
    ? explicitEnd
    : new Date(start.getTime() + DEBRIEF_DEFAULT_DURATION_MINUTES[eventType] * 60_000);
  const eventId = `event-${stableToken(`${caseItem.id}:${followUp.date}:${followUp.time}:${followUp.purpose}`)}`;
  const timestamp = now.toISOString();

  return {
    id: `debrief-reminder-${stableToken(`${caseItem.id}:${eventId}`)}`,
    eventId,
    caseId: caseItem.id,
    eventType,
    eventLabel: debriefEventLabel(eventType),
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    endTimeInferred: !explicitEnd || explicitEnd <= start,
    status: now >= end ? "pending" : "scheduled",
    visibleAt: end.toISOString(),
    createdAt: timestamp,
    updatedAt: timestamp
  };
};

export const isCurrentFollowUpDebrief = (caseItem: RealEstateCase, reminder: DebriefReminder) =>
  Boolean(caseItem.followUp && buildDebriefReminder(caseItem, caseItem.followUp)?.eventId === reminder.eventId);

export const reconcileCaseDebrief = (caseItem: RealEstateCase, now = new Date()): RealEstateCase => {
  let reminders = caseItem.debriefReminders ?? [];
  let changed = false;

  reminders = reminders.map((reminder) => {
    if (reminder.status === "scheduled" && now >= new Date(reminder.endAt)) {
      changed = true;
      return { ...reminder, status: "pending" as const, visibleAt: reminder.endAt, updatedAt: now.toISOString() };
    }
    return reminder;
  });

  if (caseItem.followUp) {
    const candidate = buildDebriefReminder(caseItem, caseItem.followUp, now);
    if (candidate) {
      const existing = reminders.find((item) => item.eventId === candidate.eventId);
      if (!existing) {
        reminders = [candidate, ...reminders.filter((item) => item.status !== "scheduled" || Boolean(item.activityId))];
        changed = true;
      } else if (
        (existing.status === "scheduled" || existing.status === "pending" || existing.status === "snoozed") &&
        !existing.startedAt &&
        (existing.startAt !== candidate.startAt || existing.endAt !== candidate.endAt)
      ) {
        reminders = reminders.map((item) => item.id !== existing.id ? item : {
          ...item,
          startAt: candidate.startAt,
          endAt: candidate.endAt,
          endTimeInferred: candidate.endTimeInferred,
          status: candidate.status,
          visibleAt: candidate.endAt,
          updatedAt: now.toISOString()
        });
        changed = true;
      }
    }
  }

  return changed ? { ...caseItem, debriefReminders: reminders, updatedAt: now.toISOString() } : caseItem;
};

export const activateCompletedEventDebrief = (
  caseItem: RealEstateCase,
  completedFollowUp: FollowUp,
  activityId: string,
  now = new Date()
) => {
  const candidate = buildDebriefReminder(caseItem, completedFollowUp, now);
  if (!candidate) return caseItem;
  const existing = (caseItem.debriefReminders ?? []).find((item) => item.eventId === candidate.eventId);
  const reminder = {
    ...(existing ?? candidate),
    activityId,
    status: "pending" as const,
    visibleAt: now.toISOString(),
    updatedAt: now.toISOString()
  };
  return {
    ...caseItem,
    debriefReminders: [reminder, ...(caseItem.debriefReminders ?? []).filter((item) => item.eventId !== reminder.eventId)],
    updatedAt: now.toISOString()
  };
};

export const resolveDebriefWithoutCompletion = (
  caseItem: RealEstateCase,
  reminderId: string,
  reason: DebriefNotRequiredReason,
  note = "",
  resolvedAt = new Date().toISOString(),
  title = "Debrief označený ako nepotrebný"
): RealEstateCase => {
  const reminder = caseItem.debriefReminders?.find((item) => item.id === reminderId);
  if (!reminder || reminder.status === "completed" || reminder.status === "not_required") return caseItem;
  const closesCurrentMeeting = isCurrentFollowUpDebrief(caseItem, reminder);
  return {
    ...caseItem,
    followUp: closesCurrentMeeting ? undefined : caseItem.followUp,
    nextStep: closesCurrentMeeting ? "Chýba ďalší krok" : caseItem.nextStep,
    debriefReminders: caseItem.debriefReminders?.map((item) => item.id !== reminderId ? item : {
      ...item,
      status: "not_required",
      notRequiredAt: resolvedAt,
      notRequiredReason: reason,
      notRequiredNote: note || undefined,
      updatedAt: resolvedAt
    }),
    timeline: [{
      id: `activity-${stableToken(`${reminderId}:${resolvedAt}:${reason}`)}`,
      createdAt: resolvedAt,
      type: "debrief",
      title,
      body: `${DEBRIEF_REASON_LABELS[reason]}${note ? `: ${note}` : "."}`
    }, ...caseItem.timeline],
    updatedAt: resolvedAt
  };
};

export const isUnresolvedRequiredDebrief = (reminder: DebriefReminder, now = new Date()) => {
  if (reminder.status === "completed" || reminder.status === "not_required") return false;
  const endAt = new Date(reminder.endAt).getTime();
  return Number.isFinite(endAt) && endAt <= now.getTime();
};

export const getPendingRequiredDebrief = (caseItem: RealEstateCase, now = new Date()) =>
  [...(caseItem.debriefReminders ?? [])]
    .filter((reminder) => isUnresolvedRequiredDebrief(reminder, now))
    .sort((first, second) => first.endAt.localeCompare(second.endAt))[0];

export const isCaseWorkflowGatedByDebrief = (caseItem: RealEstateCase, now = new Date()) =>
  Boolean(getPendingRequiredDebrief(caseItem, now));

export const incompleteDebriefs = (cases: RealEstateCase[], now = new Date()) => cases.flatMap((caseItem) =>
  (caseItem.debriefReminders ?? [])
    .filter((reminder) => {
      if (!isUnresolvedRequiredDebrief(reminder, now)) return false;
      return reminder.status !== "snoozed" || now >= new Date(reminder.visibleAt);
    })
    .map((reminder) => ({ caseItem, reminder }))
).sort((a, b) => a.reminder.endAt.localeCompare(b.reminder.endAt));

export const debriefElapsedLabel = (endAt: string, now = new Date()) => {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(endAt).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min po stretnutí`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min po stretnutí` : `${hours} h po stretnutí`;
};

export const debriefMeetingTimeLabel = (startAt: string, now = new Date()) => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return "";
  const sameDay = start.getFullYear() === now.getFullYear() && start.getMonth() === now.getMonth() && start.getDate() === now.getDate();
  const time = new Intl.DateTimeFormat("sk-SK", { hour: "2-digit", minute: "2-digit" }).format(start);
  if (sameDay) return `Dnes ${time}`;
  const date = new Intl.DateTimeFormat("sk-SK", { day: "numeric", month: "numeric", year: "numeric" }).format(start);
  return `${date} · ${time}`;
};
